/**
 * Computes the viewer's actual current best hand on both axes — rank and
 * jewel/"color" — live, client-side, from whatever grid cells are revealed
 * so far. Uses @pokercubed/shared's evaluatePlayerRankHand/JewelHand — the
 * exact functions the engine scores the showdown with — so this can never
 * show a different hand from the one that's paid (e.g. a line a special card
 * rules out, or a special played as plain while effects are off).
 */

import { useMemo } from 'react';
import {
  HandEvaluator,
  isRealCard,
  GRID_LINES,
  evaluatePlayerRankHand,
  evaluatePlayerJewelHand,
  playerLines,
  resolveForView,
  withoutSpecials,
  type Card,
  type HandEvaluation,
  type JewelHandEvaluation,
  type GridCard as SharedGridCard,
  type GridPosition,
} from '@pokercubed/shared';
import { useGameStore } from '../store/gameStore';

/** Which of the 8 numbered paylines (matching CommunityGrid's badges) a `winningLine` is — 1-8, or null if it somehow doesn't match one of the 8. */
export function getLineNumber(winningLine: GridPosition[]): number | null {
  const index = GRID_LINES.findIndex(
    (line) => line.length === winningLine.length && line.every((pos, i) => pos === winningLine[i])
  );
  return index === -1 ? null : index + 1;
}

/**
 * Before any line is fully revealed, there's no real HandEvaluation yet —
 * this is the provisional stand-in: the strongest CURRENT (locked-in-only,
 * never speculative) line among all 8, so the headline shows something
 * useful instead of "Not yet determined" while a hand is still forming.
 */
export interface ProvisionalHand {
  name: string;
  cards: Card[];
  winningLine: GridPosition[];
}

export interface BestHandsSoFar {
  rank: (HandEvaluation & { winningLine: GridPosition[] }) | ProvisionalHand | null;
  jewel: (JewelHandEvaluation & { winningLine: GridPosition[] }) | ProvisionalHand | null;
}

/** Picks the strongest CURRENT (not speculative) rank-axis line among the player's lines, for use once no line is fully revealed yet. */
function bestCurrentRankLine(
  holeCards: Card[],
  revealedGridCards: SharedGridCard[],
  lines: readonly GridPosition[][]
): ProvisionalHand | null {
  const cardAt = new Map(revealedGridCards.map((g) => [g.position, g.card] as const));
  let best: { line: GridPosition[]; strength: number; label: string } | null = null;

  for (const line of lines) {
    const cells = line.map((position) => ({ position, card: cardAt.get(position) ?? null }));
    const potential = HandEvaluator.evaluateLinePotential(holeCards, line, cells);
    if (!best || potential.strength > best.strength) {
      best = { line: [...line], strength: potential.strength, label: potential.label };
    }
  }

  if (!best || best.strength <= 0) return null;

  const cards = [
    ...holeCards,
    ...best.line.map((position) => cardAt.get(position)).filter((c): c is Card => c !== undefined),
  ];

  return { name: best.label, cards, winningLine: best.line };
}

/** Jewel-axis counterpart of bestCurrentRankLine. */
function bestCurrentJewelLine(
  holeCards: Card[],
  revealedGridCards: SharedGridCard[],
  lines: readonly GridPosition[][]
): ProvisionalHand | null {
  const cardAt = new Map(revealedGridCards.map((g) => [g.position, g.card] as const));
  let best: { line: GridPosition[]; strength: number; label: string } | null = null;

  for (const line of lines) {
    const cells = line.map((position) => ({ position, card: cardAt.get(position) ?? null }));
    const potential = HandEvaluator.evaluateJewelLinePotential(holeCards, line, cells);
    if (!best || potential.strength > best.strength) {
      best = { line: [...line], strength: potential.strength, label: potential.label };
    }
  }

  if (!best || best.strength <= 0) return null;

  const cards = [
    ...holeCards,
    ...best.line.map((position) => cardAt.get(position)).filter((c): c is Card => c !== undefined),
  ];

  return { name: best.label, cards, winningLine: best.line };
}

export function useBestHandsSoFar(): BestHandsSoFar {
  const communityCards = useGameStore((state) => state.communityCards);
  const players = useGameStore((state) => state.players);
  const yourSeat = useGameStore((state) => state.yourSeat);
  const specialEffects = useGameStore((state) => state.specialCardEffects);

  return useMemo(() => {
    const none: BestHandsSoFar = { rank: null, jewel: null };

    if (yourSeat === null) return none;

    const you = players.find((p) => p.seatPosition === yourSeat);
    if (!you?.holeCards || you.holeCards.length !== 2) return none;

    // The viewer's own hole cards are always real — never hidden/masked.
    const dealtCards = you.holeCards.filter(isRealCard);
    if (dealtCards.length !== 2) return none;

    // Every cell you can see: revealed, or privately (the Seer's early look at the centre).
    const revealedGridCards: SharedGridCard[] = communityCards
      .filter((c) => c.card != null) // a face-down cell has no card (undefined), not null
      .map((c) => ({
        position: c.position as GridPosition,
        card: c.card as Card,
        revealed: true,
      }));

    // Exactly how the engine will score it: the player's own lines, and the
    // cards as they actually play (plain, while special effects are off).
    // Impersonator copies a centre you can already see (Seer); Limbo resolves once the whole matrix is known.
    const holeCards = resolveForView(dealtCards, revealedGridCards, specialEffects);
    const lines = playerLines(holeCards, specialEffects);
    const playingCards = specialEffects ? holeCards : withoutSpecials(holeCards);

    let rank: BestHandsSoFar['rank'] = null;
    try {
      rank = evaluatePlayerRankHand(holeCards, revealedGridCards, specialEffects);
    } catch {
      // No line is fully revealed yet (true before the diamond reveal) — fall
      // back to the strongest current line instead of "Not yet determined".
      rank = bestCurrentRankLine(playingCards, revealedGridCards, lines);
    }

    let jewel: BestHandsSoFar['jewel'] = null;
    try {
      jewel = evaluatePlayerJewelHand(holeCards, revealedGridCards, specialEffects);
    } catch {
      jewel = bestCurrentJewelLine(playingCards, revealedGridCards, lines);
    }

    return { rank, jewel };
  }, [communityCards, players, yourSeat, specialEffects]);
}
